import React, { useState, useEffect } from "react";
import { db } from "../../firebase/firebase";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import "./AllInventory.css";
import { convertTimestampToDateTime } from "../../utils/ProductStatus";
import FileOpenSharpIcon from "@mui/icons-material/FileOpenSharp";
import { useNavigate } from "react-router-dom";
import data from "../../assets/db/main-db.json";

const AllInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const inventoryCollection = collection(db, "all-products");
        const inventorySnapshot = await getDocs(inventoryCollection);
        const inventoryList = inventorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filtered = inventoryList.filter((inventory) => {
          const history = inventory.history || [];
          const firstStatus = history[0]?.status;
          return history.length === 1 && firstStatus === "inventory";
        });

        setInventory(filtered);
        setFilteredInventory(filtered);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const markAsTransferred = async (productId) => {
    try {
      const productRef = doc(db, "all-products", productId);
      const productSnapshot = await getDoc(productRef);

      if (!productSnapshot.exists()) {
        console.error("Product not found!");
        return;
      }

      const productData = productSnapshot.data();
      const existingHistory = productData.history || [];
      const updatedHistory = [
        ...existingHistory,
        { status: "transferred", timestamp: new Date() },
      ];

      await updateDoc(productRef, { history: updatedHistory });

      setInventory((prevInventory) =>
        prevInventory.map((item) =>
          item.id === productId ? { ...item, history: updatedHistory } : item
        )
      );

      console.log("Product successfully marked as transferred!");
    } catch (err) {
      console.error("Error updating product status:", err);
    }
  };

  useEffect(() => {
    let filteredData = inventory;

    if (searchQuery) {
      filteredData = filteredData.filter(
        (item) =>
          item.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.variant.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.imei_1.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.imei_2.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.serialNumber &&
            item.serialNumber
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (item.condition &&
            item.condition.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterDate) {
      filteredData = filteredData.filter((item) => {
        if (!item.createdAt || !item.createdAt.seconds) return false;
        const itemDate = new Date(item.createdAt.seconds * 1000);
        const selectedDate = new Date(filterDate);
        return itemDate.toDateString() === selectedDate.toDateString();
      });
    }

    setFilteredInventory(filteredData);
  }, [searchQuery, filterDate, inventory]);

  if (loading) return <div>Loading Inventory...</div>;
  if (error) return <div>Error: {error}</div>;

  const groupedInventory = filteredInventory.reduce((acc, item) => {
    (acc[item.brand] = acc[item.brand] || []).push(item);
    return acc;
  }, {});

  return (
    <div className="inventory-container">
      <div className="search-filter-containern flex">
        <div>
          <input
            type="text"
            placeholder="Search by model, variant, or IMEI"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-inputb w-40 h-8 m-3 text-black rounded p-2"
          />
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="date-input w-40 h-8 m-3 text-black rounded p-2"
          />
        </div>
        <div className="flex ml-40">
          <div className="summary-item text-center mr-10">
            <h3 className="text-xl font-bold">Total Products</h3>
            <p className="text-lg">{filteredInventory.length}</p>
          </div>
          <div className="summary-item text-center">
            <h3 className="text-xl font-bold">Total Brands</h3>
            <p className="text-lg">
              {
                Object.keys(
                  filteredInventory.reduce((acc, item) => {
                    acc[item.brand] = true;
                    return acc;
                  }, {})
                ).length
              }
            </p>
          </div>
        </div>

        <div className="ml-40">
          <button className="px-5" onClick={() => navigate("/export-csv")}>
            <FileOpenSharpIcon />
            <span className="ml-2">Export CSV</span>
          </button>
        </div>
      </div>
      {Object.keys(groupedInventory).map((brand) => (
        <div key={brand} className="brand-group-inv">
          <h2 className="bg-red-600 py-1 flex items-center justify-center text-xl rounded-lg">
            {brand}
          </h2>
          <div className="card-container-inv">
            {groupedInventory[brand].map((item) => (
              <InventoryCard
                key={item.id}
                item={item}
                markAsTransferred={markAsTransferred}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AllInventory;

const InventoryCard = ({ item, markAsTransferred }) => {
  // Find the model image from the JSON database
  const brandData = data.brands.find((brand) =>
    brand.models.some((model) => model.name === item.model)
  );
  const modelData = brandData?.models.find(
    (model) => model.name === item.model
  );
  const imageUrl = modelData?.imageUrl || "https://via.placeholder.com/100";

  return (
    <div className="bg-white shadow-md rounded-lg p-3 w-72 border border-gray-200 flex flex-col items-center">
      <div className="flex w-full justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">{item.model}</h3>
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            item.condition === "Gold"
              ? "bg-yellow-500 text-white"
              : item.condition === "Platinum"
              ? "bg-gray-300 text-black"
              : item.condition === "Silver"
              ? "bg-gray-400 text-white"
              : "bg-yellow-300 text-black"
          }`}
        >
          {item.condition}
        </span>
      </div>
      <div className="flex-1 text-gray-700 text-xs space-y-1 w-full flex ">
        <div>
          <p>
            <strong className="text-sm mb-1">Variant:</strong> {item.variant}
          </p>
          <p>
            <strong>Serial Number:</strong>
            <Link
              to={`/product-details/${item.serialNumber}`}
              style={{
                textDecoration: "none",
                color: "blue",
                marginRight: "5px",
              }}
            >
              {item.serialNumber}
            </Link>
          </p>
          <p>
            <strong className="text-sm mb-1">IMEI 1:</strong> {item.imei_1}
          </p>
          <p>
            <strong className="text-sm mb-1">IMEI 2:</strong> {item.imei_2}
          </p>
          {/* <p>
            <strong className="text-sm mb-1">MRP:</strong>{" "}
            {item.mrp ? `₹${item.mrp}` : "N/A"}
          </p>
          <p>
            <strong className="text-sm mb-1">Distributor Price:</strong>{" "}
            {item.mrp ? `₹${item.distributorPrice}` : "N/A"}
          </p>
          <p>
            <strong className="text-sm mb-1">Retailer Price:</strong>{" "}
            {item.retailerPrice ? `₹${item.retailerPrice}` : "N/A"}
          </p> */}
        </div>
        <div>
          {/* Phone Image */}
          <img
            src={imageUrl}
            alt={item.model}
            className="w-20 h-20 object-contain rounded-md mt-3 mix-blend-multiply"
          />
        </div>
      </div>
      <button
        onClick={() => markAsTransferred(item.id)}
        disabled={item.history.some((entry) => entry.status === "transferred")}
        className={`w-full mt-3 py-2 text-xs rounded text-white font-semibold ${
          item.history.some((entry) => entry.status === "transferred")
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-red-600 hover:bg-red-700"
        }`}
      >
        {item.history.some((entry) => entry.status === "transferred")
          ? "Transferred"
          : "Mark as Transferred"}
      </button>
    </div>
  );
};
